/**
 * Data auditing utility to detect and log data inconsistencies
 * This helps identify potential data quality issues in the application
 */

// Constants for log levels
const LOG_LEVELS = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Maximum number of data issues to store in memory
const MAX_ISSUES_TO_STORE = 100;

// In-memory storage for data issues (for debugging)
let dataIssues = [];

/**
 * Log a data issue
 * @param {string} entityType - Type of entity (store, coupon, category)
 * @param {string} entityId - ID or identifier of the entity
 * @param {string} issue - Description of the issue
 * @param {string} level - Log level (error, warning, info)
 */
const logDataIssue = (entityType, entityId, issue, level = LOG_LEVELS.WARNING) => {
  const issueRecord = {
    timestamp: new Date().toISOString(),
    entityType,
    entityId,
    issue,
    level
  };
  
  // Log to console with appropriate level
  switch (level) {
    case LOG_LEVELS.ERROR:
      console.error(`DATA ERROR [${entityType}:${entityId}]: ${issue}`);
      break;
    case LOG_LEVELS.WARNING:
      console.warn(`DATA WARNING [${entityType}:${entityId}]: ${issue}`);
      break;
    default:
      console.info(`DATA INFO [${entityType}:${entityId}]: ${issue}`);
  }
  
  // Store in memory for reporting
  dataIssues.push(issueRecord);
  
  // Keep array size under control
  if (dataIssues.length > MAX_ISSUES_TO_STORE) {
    dataIssues.shift(); // Remove oldest issue
  }
};

/**
 * Data audit service
 */
export const dataAuditService = {
  // Constants
  LOG_LEVELS,
  
  // Log a data issue
  logIssue: logDataIssue,
  
  // Get all logged issues
  getIssues: () => [...dataIssues],
  
  // Clear logged issues
  clearIssues: () => {
    dataIssues = [];
  },
  
  // Check store data consistency
  validateStore: (store) => {
    if (!store) {
      logDataIssue('store', 'unknown', 'Null or undefined store object', LOG_LEVELS.ERROR);
      return false;
    }
    
    let isValid = true;
    
    // Check required fields
    if (!store.id) {
      logDataIssue('store', 'unknown', 'Missing store ID', LOG_LEVELS.ERROR);
      isValid = false;
    }
    
    if (!store.name) {
      logDataIssue('store', store.id || 'unknown', 'Missing store name', LOG_LEVELS.WARNING);
      isValid = false;
    }
    
    if (!store.url_slug) {
      logDataIssue('store', store.id || 'unknown', 'Missing URL slug', LOG_LEVELS.WARNING);
      isValid = false;
    } else if (!/^[a-z0-9-]+$/.test(store.url_slug)) {
      logDataIssue('store', store.id || 'unknown', 'Invalid URL slug format', LOG_LEVELS.WARNING);
      isValid = false;
    }
    
    return isValid;
  },
  
  // Check coupon data consistency
  validateCoupon: (coupon) => {
    if (!coupon) {
      logDataIssue('coupon', 'unknown', 'Null or undefined coupon object', LOG_LEVELS.ERROR);
      return false;
    }
    
    let isValid = true;
    
    // Check required fields
    if (!coupon.id) {
      logDataIssue('coupon', 'unknown', 'Missing coupon ID', LOG_LEVELS.ERROR);
      isValid = false;
    }
    
    if (!coupon.title) {
      logDataIssue('coupon', coupon.id || 'unknown', 'Missing coupon title', LOG_LEVELS.WARNING);
      isValid = false;
    }
    
    if (!coupon.store_id) {
      logDataIssue('coupon', coupon.id || 'unknown', 'Missing store ID reference', LOG_LEVELS.ERROR);
      isValid = false;
    }
    
    // Check date format
    if (coupon.expiry_date) {
      const expiryDate = new Date(coupon.expiry_date);
      if (isNaN(expiryDate.getTime())) {
        logDataIssue('coupon', coupon.id || 'unknown', 'Invalid expiry date format', LOG_LEVELS.WARNING);
        isValid = false;
      }
    }
    
    return isValid;
  },
  
  // Check category data consistency
  validateCategory: (category) => {
    if (!category) {
      logDataIssue('category', 'unknown', 'Null or undefined category object', LOG_LEVELS.ERROR);
      return false;
    }
    
    let isValid = true;
    
    // Check required fields
    if (!category.id) {
      logDataIssue('category', 'unknown', 'Missing category ID', LOG_LEVELS.ERROR);
      isValid = false;
    }
    
    if (!category.name) {
      logDataIssue('category', category.id || 'unknown', 'Missing category name', LOG_LEVELS.WARNING);
      isValid = false;
    }
    
    if (!category.url_slug) {
      logDataIssue('category', category.id || 'unknown', 'Missing URL slug', LOG_LEVELS.WARNING);
      isValid = false;
    } else if (!/^[a-z0-9-]+$/.test(category.url_slug)) {
      logDataIssue('category', category.id || 'unknown', 'Invalid URL slug format', LOG_LEVELS.WARNING);
      isValid = false;
    }
    
    return isValid;
  },
  
  // Check relationship consistency between entities
  validateRelationship: (sourceEntity, sourceType, targetId, targetType) => {
    if (!sourceEntity || !sourceEntity.id) {
      logDataIssue(sourceType, 'unknown', `Invalid source entity for ${targetType} relationship`, LOG_LEVELS.ERROR);
      return false;
    }
    
    if (!targetId) {
      logDataIssue(sourceType, sourceEntity.id, `Missing ${targetType} reference ID`, LOG_LEVELS.ERROR);
      return false;
    }
    
    return true;
  },
  
  // Get summary statistics of data issues
  getIssueStats: () => {
    const stats = {
      total: dataIssues.length,
      errors: dataIssues.filter(issue => issue.level === LOG_LEVELS.ERROR).length,
      warnings: dataIssues.filter(issue => issue.level === LOG_LEVELS.WARNING).length,
      info: dataIssues.filter(issue => issue.level === LOG_LEVELS.INFO).length,
      byEntityType: {}
    };
    
    // Count issues by entity type
    dataIssues.forEach(issue => {
      if (!stats.byEntityType[issue.entityType]) {
        stats.byEntityType[issue.entityType] = 0;
      }
      stats.byEntityType[issue.entityType]++;
    });
    
    return stats;
  }
};
